import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { merge, Observable, Subject, Subscriber, TimeoutError } from 'rxjs';
import { debounceTime, filter, take, takeUntil, timeout } from 'rxjs/operators';
import { FilterValue } from '../../../../app/interfaces/base/filter-value';
import { FormControl } from '@angular/forms';
import { ApiService } from '../../../../app/services/base/api.service';
import { NationaalGeoregisterLocatieServerDocs, NationaalGeoregisterSuggest } from '../../../../app/interfaces/external-services/nationaal-georegister-suggest';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { NationaalGeoregisterLookup } from '../../../../app/interfaces/external-services/nationaal-georegister-lookup';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AuthenticationService } from '../../../../app/services/base/authentication.service';
import { Organisation } from '../../../../app/interfaces/routering/organisation';

interface AddressSearch {
  address?: string;
  search?: string;
}

@Component({
  selector: 'fury-table-filter-address-autocomplete',
  templateUrl: './table-filter-address-autocomplete.component.html',
  styleUrls: ['./table-filter-address-autocomplete.component.scss']
})

export class TableFilterAddressAutocompleteComponent implements AfterViewInit, OnDestroy {

  public isFiltered: boolean = false;
  public isLoading: boolean = false;

  @Input() name: string = null;

  @Input() customFiltering: boolean = false;
  @Output() clearFilter: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() filterChange: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('filterAutocompleteTrigger', {read: MatAutocompleteTrigger}) filterAutocompleteTrigger: MatAutocompleteTrigger;

  public element: FormControl = new FormControl<string>('');

  public searchedAddressSuggestions: NationaalGeoregisterLocatieServerDocs[] = null;
  public hasAddressSuggestionTimedOut: boolean = false;
  public hasAddressSuggestionNoResults: boolean = false;

  private filter$: Subject<string> = new Subject<string>();
  private destroy$: Subject<null> = new Subject<null>();

  constructor(private api: ApiService,
              private http: HttpClient,
              private authenticationService: AuthenticationService,
  ) {
  }

  @HostListener('keydown.space', ['$event'])
  @HostListener('click', ['$event']) onClick(e: Event): void {
    e.stopPropagation();
  }

  ngAfterViewInit(): void {
    const valueChanges$ = this.element.valueChanges;

    merge(
      valueChanges$.pipe(
        filter((value: string | AddressSearch): boolean => typeof value === 'string'),
        debounceTime(500)
      ),
      valueChanges$.pipe(
        filter((value: string | AddressSearch): boolean => typeof value !== 'string'),
      )
    ).pipe(
      takeUntil(this.destroy$)
    ).subscribe((value: string | AddressSearch): void => {
      if (typeof value === 'string') {
        this.startSearchingForAddress(value);
      } else if (typeof value.address !== 'undefined' || typeof value.search !== 'undefined') {
        this.isFiltered = !!value;

        if (typeof value.search !== 'undefined') {
          this.filter$.next(value.search);
          this.element.setValue(value.search, {emitEvent: false});
        }
      }
    });

    this.filter$.subscribe((value: string): void => {
      if (this.customFiltering) {
        (value ? this.filterChange.emit(value) : this.clearFilter.next());
      } else {
        (value ? this.api.setFilter(this.name, value) : this.api.clearFilter(this.name));
      }
    });

    this.api.preSetFilters.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe((filters: FilterValue[]): void => {
      filters.forEach((filterValue: FilterValue): void => {
        if (this.name === filterValue.name) {
          this.isFiltered = !!filterValue.value;

          if (filterValue.value) {
            this.element.setValue(filterValue.value, {emitEvent: false});
          }
        }
      });
    });

    this.api.filterCleared.pipe(takeUntil(this.destroy$)).subscribe((): void => this.clearFilterButton());
    this.api.clearAllFilters.pipe(takeUntil(this.destroy$)).subscribe((): void => this.clearFilterButton(false));
  }

  startSearchingForAddress(value: string): void {
    this.isLoading = true;

    this.getSearchedAddress(value).subscribe((response: NationaalGeoregisterSuggest): void => {
      if (response) {
        if (response.response.numFound > 1) {
          this.searchedAddressSuggestions = response.response.docs;
        } else if (response.response.numFound === 1) {
          this.selectSearchedAddress(response.response.docs[0].id);
        } else {
          this.hasAddressSuggestionNoResults = true;
        }
      }

      this.isLoading = false;
    });
  }

  getSearchedAddress(value: string): Observable<NationaalGeoregisterSuggest> {
    return new Observable<NationaalGeoregisterSuggest>((o: Subscriber<NationaalGeoregisterSuggest>): void => {
      this.clearAddressSuggestions();
      this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((organisation: Organisation): void => {
        const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest' +
          '?q=' + encodeURIComponent(value) +
          '&rows=10' +
          '&bq=' + encodeURIComponent('type:provincie^0.5') +
          '&bq=' + encodeURIComponent('type:gemeente^0.5') +
          '&bq=' + encodeURIComponent('type:woonplaats^0.5') +
          '&bq=' + encodeURIComponent('type:weg^0.5') +
          '&bq=' + encodeURIComponent('type:adres^1.5') +
          '&fq=bron:"BAG"' +
          (organisation ?
            '&fq=gemeentenaam:(' + organisation.center_municipality + ')'
            : '');

        this.http.get(url, {
          observe: 'response',
          responseType: 'json'
        }).pipe(
          takeUntil(this.destroy$),
          timeout(5000)
        ).subscribe(
          (response: HttpResponse<NationaalGeoregisterSuggest>): void => {
            o.next(response.body);
          },
          (error): void => {
            if (error instanceof TimeoutError) {
              this.hasAddressSuggestionTimedOut = true;
            }
            o.next(undefined);
          });
      });
    });
  }

  selectSearchedAddress(id: string): void {
    if (typeof id !== 'undefined') {
      this.isLoading = true;

      const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup' +
        '?id=' + encodeURIComponent(id);

      this.http.get(url, {
        observe: 'response',
        responseType: 'json'
      }).pipe(
        takeUntil(this.destroy$),
        timeout(5000)
      ).subscribe(
        (response: HttpResponse<NationaalGeoregisterLookup>): void => {
          if (response.body.response.numFound > 0) {
            const data = response.body.response.docs[0],
              address: string = (data.type === 'woonplaats'
                  ? (typeof data.woonplaatsnaam !== 'undefined' ? data.woonplaatsnaam : '')
                  : (typeof data.straatnaam !== 'undefined' && data.straatnaam !== null ? data.straatnaam : data.weergavenaam) +
                  (typeof data.huisnummer !== 'undefined' && data.huisnummer !== null ? ' ' + data.huisnummer : '') +
                  (typeof data.huisletter !== 'undefined' && data.huisletter !== null ? data.huisletter : '')
              );

            this.filter$.next(address);
            this.element.setValue(address, {emitEvent: false});
            this.isFiltered = true;
          }

          this.isLoading = false;
        }
      );
    }
  }

  clearAddressSuggestions(): void {
    this.hasAddressSuggestionTimedOut = false;
    this.hasAddressSuggestionNoResults = false;
  }

  formatAutocomplete(result: any): string {
    return result !== null && typeof result === 'object' ? result.address : result;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clearFilterButton(emitEvent: boolean = true): void {
    this.element.setValue('', {emitEvent: false});
    this.isFiltered = false;
    this.searchedAddressSuggestions = [];

    if (emitEvent) {
      if (this.customFiltering) {
        this.clearFilter.next();
      } else {
        this.api.clearFilter(this.name);
      }
    }
  }
}
